import { useEffect, useState } from 'react';
import type { ConditionalVisibility, FieldDef, FieldOption, FormSettings, FormType } from '@jfh/intake-shared';
import {
  FIELD_TYPE_CATALOGUE,
  companyLockTargets,
  fieldReferences,
  resolveStaticOptions,
} from '@jfh/intake-shared';
import { loadList } from '../canvas/useFormPreviewData.js';
import { api } from '../../lib/api.js';
import { useIsAdmin } from '../../lib/useIsAdmin.js';
import { useIqSpecFields } from '../../lib/useIqSpecFields.js';
import { Icon } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import { ChoicesSection } from './ChoicesSection.js';
import { connectionsSummary, moreSummary, ruleCandidates } from './logic.js';
import { Connections, MoreOptions } from './MoreSections.js';
import { RuleSentence } from './RuleSentence.js';
import { AddressLines, CompanyLookupSection, RepeatSection } from './TypeSections.js';
import { Collapsible, ToggleRow } from './ui.js';

const CHOICE_TYPES = ['select', 'radio', 'checkbox', 'multiselect'];
const NO_REQUIRED = ['heading', 'address', 'repeater', 'contact', 'hidden'];
const PLACEHOLDER_TYPES = ['short_text', 'long_text', 'email', 'phone', 'number', 'vat', 'sort_code', 'company_number'];

export interface QuestionPanelProps {
  field: FieldDef | null;
  fields: FieldDef[];
  formType: FormType;
  settings: FormSettings;
  update: (patch: Partial<FieldDef>) => void;
  onSettingsChange: (patch: Partial<FormSettings>) => void;
  onClose: () => void;
  onMove: (dir: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  /** Bumped by the canvas toolbar's Delete: show the "what depends on it" check. */
  deleteAsk?: number;
}

/**
 * Country lookup attributes (e.g. D_EUCountry) that rules can depend on, and
 * the values each takes in iQ's country list (offered as answers).
 */
function useCountryAttrs(): { attrs: string[]; values: Record<string, string[]> } {
  const [attrs, setAttrs] = useState<string[]>([]);
  const [values, setValues] = useState<Record<string, string[]>>({});
  useEffect(() => {
    let live = true;
    api
      .listLookups()
      .then((r) => {
        const c = r.lookups.find((d) => d.key === 'countries');
        if (live) setAttrs((c?.extraFields ?? []).map((e) => e.name));
      })
      .catch(() => undefined);
    api
      .getLookup('countries')
      .then((r) => {
        const seen: Record<string, Set<string>> = {};
        for (const o of r.options) {
          for (const [k, v] of Object.entries(o.data ?? {})) if (v !== '') (seen[k] ??= new Set()).add(v);
        }
        const out: Record<string, string[]> = {};
        // Only short lists (yes/no style) make sense as answer buttons.
        for (const [k, set] of Object.entries(seen)) if (set.size <= 6) out[k] = [...set].sort();
        if (live) setValues(out);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  return { attrs, values };
}

/** The iQ lists the form's questions use, so rules can offer their answers. */
function useIqLists(fields: FieldDef[]): Record<string, FieldOption[]> {
  const keys = [...new Set(fields.flatMap((x) => (x.optionsSource?.type === 'iq' ? [x.optionsSource.key] : [])))].sort();
  const joined = keys.join('|');
  const [lists, setLists] = useState<Record<string, FieldOption[]>>({});
  useEffect(() => {
    let live = true;
    for (const key of joined ? joined.split('|') : []) {
      void loadList(key).then((options) => {
        if (live) setLists((cur) => (cur[key] === options ? cur : { ...cur, [key]: options }));
      });
    }
    return () => {
      live = false;
    };
  }, [joined]);
  return lists;
}

function EmptyPanel() {
  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: '32px 8px 20px',
          color: 'var(--text-3)',
        }}
      >
        <Icon name="forms" size={22} />
        <div style={{ fontWeight: 700, color: 'var(--text-2)', fontSize: 15, marginTop: 12 }}>
          Pick a question to change it
        </div>
        <div style={{ marginTop: 4, fontSize: 13, maxWidth: 280 }}>
          Click any question on the form. Its wording, answers and rules appear here.
        </div>
      </div>
      <div className="card" style={{ padding: '14px 16px', display: 'grid', gap: 10, fontSize: 12.5, color: 'var(--text-2)' }}>
        <div style={{ display: 'flex', gap: 9 }}>
          <Icon name="plus" size={14} />
          <span>
            Hover between two questions and press <strong>Add a question here</strong>.
          </span>
        </div>
        <div style={{ display: 'flex', gap: 9 }}>
          <Icon name="drag" size={14} />
          <span>
            Drag a question to move it, or select it and press <span className="kbd">Alt</span> +{' '}
            <span className="kbd">↑</span> / <span className="kbd">↓</span>.
          </span>
        </div>
        <div style={{ display: 'flex', gap: 9 }}>
          <Icon name="send" size={14} />
          <span>
            <strong>Try it out</strong> fills in the draft as an applicant, rules and all.
          </span>
        </div>
        <div style={{ display: 'flex', gap: 9 }}>
          <Icon name="bolt" size={14} />
          <span>
            Nothing reaches applicants until you <strong>Review &amp; publish</strong>.
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The question settings panel (F9 P2, replaces Inspector): everyday settings
 * first, then the rule sentence, then More options and Connections collapsed.
 * Every setting the old panel had is still here; the form schema is unchanged.
 */
export function QuestionPanel({
  field: f,
  fields,
  formType,
  settings,
  update,
  onSettingsChange,
  onClose,
  onMove,
  onDuplicate,
  onDelete,
  deleteAsk = 0,
}: QuestionPanelProps) {
  const isAdmin = useIsAdmin();
  const toast = useToast();
  const countryAttrs = useCountryAttrs();
  const iqLists = useIqLists(fields);
  const liveFields = useIqSpecFields();
  const [confirmRefs, setConfirmRefs] = useState<string[] | null>(null);
  useEffect(() => setConfirmRefs(null), [f?.id]);
  useEffect(() => {
    if (deleteAsk > 0 && f) setConfirmRefs(fieldReferences(fields, settings, f.id));
    // Only when the canvas asks, not when the form changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteAsk]);

  if (!f) return <EmptyPanel />;

  const meta = FIELD_TYPE_CATALOGUE.find((t) => t.type === f.type);
  const t = f.type;
  const isHeading = t === 'heading';
  const isChoice = CHOICE_TYPES.includes(t);
  const isBoolCheckbox = t === 'checkbox' && !resolveStaticOptions(f).length && !f.optionsSource;
  const isRepeat = t === 'contact' || t === 'repeater';
  const candidates = ruleCandidates(f, fields, countryAttrs.attrs, countryAttrs.values, iqLists);
  const hasRules = Boolean((f.visibleWhen as ConditionalVisibility | undefined)?.rules?.length);
  const locked = companyLockTargets(fields).has(f.id);

  const labelTitle = isHeading
    ? 'Step title'
    : t === 'consent'
      ? 'What they tick to agree'
      : t === 'hidden'
        ? 'Name (for staff)'
        : 'Question';
  const placeholderTitle = isBoolCheckbox
    ? 'Text next to the tick box'
    : t === 'select'
      ? 'Text before they choose'
      : 'Example inside the box';
  const showPlaceholder = PLACEHOLDER_TYPES.includes(t) || isBoolCheckbox || t === 'select';

  const askDelete = () => {
    const refs = fieldReferences(fields, settings, f.id);
    if (refs.length) setConfirmRefs(refs);
    else onDelete();
  };

  return (
    <div className="question-panel">
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 3,
          background: 'var(--bg-surface-2)',
          borderBottom: '1px solid var(--border)',
          padding: '14px 18px 12px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 'var(--r-md)',
            background: 'var(--accent-soft)',
            color: 'var(--accent-text)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          <Icon name={meta?.icon ?? 'text'} size={16} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {meta?.plainName ?? t}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{meta?.description}</div>
        </div>
        <button type="button" className="icon-btn" onClick={onClose} title="Close" aria-label="Close question settings">
          <Icon name="x" size={16} />
        </button>
      </div>

      <div style={{ padding: '16px 18px 28px', display: 'grid', gap: 18 }}>
        {confirmRefs && (
          <div
            role="alert"
            style={{
              border: '1px solid var(--st-declined)',
              background: 'var(--danger-100)',
              borderRadius: 'var(--r-lg)',
              padding: '12px 14px',
              display: 'grid',
              gap: 8,
            }}
          >
            <div style={{ fontWeight: 800, color: 'var(--st-declined)', fontSize: 13.5 }}>
              Other parts of the form use this question
            </div>
            {confirmRefs.map((r, i) => (
              <div key={i} style={{ fontSize: 12.5 }}>
                • {r}
              </div>
            ))}
            <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
              If you delete it, fix those before publishing. Review &amp; publish will list them.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn sm" onClick={() => setConfirmRefs(null)}>
                Keep it
              </button>
              <button type="button" className="btn danger sm" onClick={onDelete}>
                Delete anyway
              </button>
            </div>
          </div>
        )}

        <section style={{ display: 'grid', gap: 12 }}>
          <div>
            <label className="field-label">{labelTitle}</label>
            {t === 'consent' ? (
              <textarea className="textarea" rows={2} value={f.label} onChange={(e) => update({ label: e.target.value })} />
            ) : (
              <input className="input" value={f.label} onChange={(e) => update({ label: e.target.value })} />
            )}
          </div>
          {t !== 'hidden' && (
            <div>
              <label className="field-label">
                {isHeading ? 'Line under the step title' : 'Help text'}{' '}
                <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>(optional)</span>
              </label>
              <input
                className="input"
                value={f.help ?? ''}
                placeholder={isHeading ? 'What this step is about, in a sentence' : 'Shown under the question'}
                onChange={(e) => update({ help: e.target.value || undefined })}
              />
            </div>
          )}
          {showPlaceholder && (
            <div>
              <label className="field-label">
                {placeholderTitle} <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>(optional)</span>
              </label>
              <input
                className="input"
                value={f.placeholder ?? ''}
                placeholder={isBoolCheckbox ? 'Uses the question if left blank' : 'Shown in grey until they type'}
                onChange={(e) => update({ placeholder: e.target.value || undefined })}
              />
            </div>
          )}
          {!NO_REQUIRED.includes(t) && (
            <ToggleRow
              boxed
              title="Required"
              hint={
                f.required
                  ? 'They can’t continue until they answer.'
                  : `They can skip it.${hasRules ? ' Only applies when it’s shown.' : ''}`
              }
              on={Boolean(f.required)}
              onToggle={() => update({ required: f.required ? undefined : true })}
            />
          )}
          {f.audience === 'customer' && !NO_REQUIRED.includes(t) && (
            <ToggleRow
              boxed
              title="Answer this first"
              hint={
                f.answerFirst
                  ? 'The questions after it on this step appear once it’s answered.'
                  : 'Turn on to hide the rest of this step until it’s answered.'
              }
              on={Boolean(f.answerFirst)}
              onToggle={() => update({ answerFirst: f.answerFirst ? undefined : true })}
            />
          )}
          {isHeading && (
            <div className="field-help" style={{ marginTop: 0 }}>
              Each step is one page of the form. Applicants see its title at the top. Delete the step to merge its
              questions into the step before.
            </div>
          )}
          {t === 'hidden' && (
            <div>
              <label className="field-label">Value to store</label>
              <input
                className="input"
                value={f.defaultValue ?? ''}
                placeholder="e.g. website"
                onChange={(e) => update({ defaultValue: e.target.value || undefined })}
              />
              <div className="field-help">Saved with every application. Applicants never see it.</div>
            </div>
          )}
          {t === 'consent' && (
            <div>
              <label className="field-label">The terms they agree to</label>
              <textarea
                className="textarea"
                rows={6}
                value={f.content ?? ''}
                placeholder="Paste your terms and conditions here"
                onChange={(e) => update({ content: e.target.value })}
              />
              <div className="field-help">
                Opens in a pop-up from “View the full terms &amp; conditions”. For a heading start a line with #, for
                bold wrap words in **two stars**, for a list start lines with -.
              </div>
            </div>
          )}
        </section>

        {isChoice && <ChoicesSection field={f} update={update} isAdmin={isAdmin} />}
        {isChoice && t === 'checkbox' && !f.optionsSource && resolveStaticOptions(f).length === 0 && (
          <div className="field-help" style={{ marginTop: -10 }}>
            With no answers it’s a single yes/no tick box.
          </div>
        )}

        {t === 'company_number' && (
          <CompanyLookupSection
            field={f}
            fields={fields}
            update={update}
            onRuleAdded={(text) => toast({ title: 'Rule added', body: text, icon: 'filter' })}
          />
        )}

        {t === 'address' && (
          <section style={{ display: 'grid', gap: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Which lines to ask for
            </div>
            <AddressLines
              address={f.address}
              onChange={(address) => update({ address })}
              fields={fields}
              note={locked ? 'This address is filled in from Companies House for limited companies.' : undefined}
            />
          </section>
        )}

        {isRepeat && <RepeatSection field={f} fields={fields} update={update} />}

        {t !== 'address' && (
          <RuleSentence
            field={f}
            fields={fields}
            candidates={candidates}
            onChange={(visibleWhen) => update({ visibleWhen })}
          />
        )}
        {t === 'address' && (
          <div className="field-help" style={{ marginTop: -6 }}>
            Show/hide rules aren’t available on addresses. Put the address after a question that has the rule instead.
          </div>
        )}

        <Collapsible title="More options" summary={moreSummary(f)}>
          <MoreOptions field={f} formType={formType} candidates={candidates} update={update} />
        </Collapsible>

        {!isHeading && (
          <Collapsible title="Connections" summary={connectionsSummary(f, formType, settings, liveFields)} icon="db">
            <Connections
              field={f}
              formType={formType}
              settings={settings}
              update={update}
              onSettingsChange={onSettingsChange}
            />
          </Collapsible>
        )}

        <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 14, flexWrap: 'wrap' }}>
          <button type="button" className="btn sm" onClick={() => onMove(-1)}>
            ↑ Up
          </button>
          <button type="button" className="btn sm" onClick={() => onMove(1)}>
            ↓ Down
          </button>
          <button type="button" className="btn sm" onClick={onDuplicate}>
            <Icon name="copy" size={13} /> Duplicate
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn ghost sm" style={{ color: 'var(--st-declined)' }} onClick={askDelete}>
            <Icon name="trash" size={13} /> {isHeading ? 'Delete step' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}
